/**
 * PublicPasswordGate — shown instead of `PublicSignInGate` when the public
 * share-view fetch reports `password_required` (Task 10): the grant exists
 * and isn't expired/revoked, it's just password-protected (BE Task 9).
 *
 * Structural sibling of `PublicSignInGate` in `publicShellChrome.tsx` — same
 * centered, full-page card chrome — but this one collects a password and
 * submits it itself, mirroring how `PublicLoginDialog` owns its own submit
 * flow rather than delegating pending/error state to the page.
 *
 * `onSubmit` is caller-supplied (the page wires it to
 * `unlockPublicShareView`) so this component never imports `@discovr/features`
 * fetch internals directly — it only knows "submit a password, get a view
 * back or a rejection whose `message` is safe to render verbatim". Both the
 * BE's 401 ("incorrect password") and 429 (rate-limit) responses arrive as a
 * plain `Error` with that exact message text — see
 * packages/features/src/campaign-detail/api/shareGrants.ts's
 * `UnlockShareViewError`.
 *
 * On success, `onUnlocked` hands the resolved payload back to the page so it
 * can render `PublicCampaignShell` directly — no second GET round-trip.
 */
import * as React from 'react';
import { useState } from 'react';
import type { PublicShareView } from '@discovr/features';

import { Lock, WarningCircle } from '../design-system/icons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export interface PublicPasswordGateProps {
  /** Campaign name from the `password_required` envelope — the only field
   *  the BE reveals before the link is unlocked. */
  campaignName: string;
  /** Submits the password. Resolves with the unlocked view on success;
   *  rejects with an `Error` whose `message` is the BE's exact copy
   *  (`"incorrect password"` on 401, the rate-limit text on 429). */
  onSubmit: (password: string) => Promise<PublicShareView>;
  /** Called once `onSubmit` resolves — the page seeds its query cache /
   *  state with the unlocked view so the shell renders without a refetch. */
  onUnlocked: (view: PublicShareView) => void;
}

export function PublicPasswordGate({
  campaignName,
  onSubmit,
  onUnlocked,
}: PublicPasswordGateProps): React.ReactElement {
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || !password) return;
    setPending(true);
    setError(null);
    try {
      const view = await onSubmit(password);
      onUnlocked(view);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to unlock this link.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      data-testid="public-password-gate"
      className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted px-6 text-center"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-card text-muted-foreground shadow-sm">
        <Lock size={26} />
      </span>
      <h1 className="text-heading-h6 font-semibold text-foreground">Enter password to view</h1>
      <p className="max-w-[380px] text-body-sm-regular text-muted-foreground">
        No account needed — just the shared password
      </p>
      {campaignName && (
        <p className="text-body-sm-medium text-foreground">{campaignName}</p>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="mt-2 flex w-full max-w-[320px] flex-col gap-3 text-left"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="public-password-gate-input" className="text-body-sm-medium text-foreground">
            Password
          </label>
          <Input
            id="public-password-gate-input"
            type="password"
            autoComplete="current-password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={pending}
            required
          />
        </div>

        {error && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
            <WarningCircle size={15} className="mt-px shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" loading={pending} disabled={!password.trim()} className="w-full">
          View page
        </Button>
      </form>

      <p className="mt-1 text-body-sm-regular text-muted-foreground">
        <a href="mailto:hello@getdiscovr.ai" className="font-semibold text-primary hover:text-primary/80">
          Contact support
        </a>
      </p>
    </div>
  );
}
